import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * E-belge servislerinin ortak tabanı.
 *
 * BaseService'ten türetilmez: e-belge uçlarının hiçbiri onun sözleşmesine
 * (POST /create, POST /list, PUT /, DELETE /{id}) uymuyor; türetmek yalnızca
 * çalışmayan metotları miras bırakırdı.
 *
 * Tüm istekler X-Skip-Global-Error-Handler taşır. Sebep: e-belge uçları iş
 * kuralı hatalarını 400 + { isSuccess: false, message } ile döndürüyor, global
 * ErrorInterceptor ise 400'de err.error.errorMessage alanını okuyor — o alan bu
 * yanıtlarda boş olduğu için alert.showValidation(undefined) hata fırlatıyor ve
 * mesaj kullanıcıya hiç ulaşmıyor. Global işleyici atlandığında interceptor
 * mesaj metnini olduğu gibi throwError ile geçiriyor; ekranlar entegratör
 * açıklamasını kırpmadan gösteriyor.
 */
export abstract class EInvoiceApiService {
  private static readonly skipGlobalError =
    new HttpHeaders().set('X-Skip-Global-Error-Handler', 'true');

  protected constructor(protected http: HttpClient, protected base: string) {}

  protected getRequest<T>(path: string, params?: HttpParams): Observable<T> {
    return this.http.get<T>(this.url(path), {
      headers: EInvoiceApiService.skipGlobalError,
      params,
    });
  }

  protected postRequest<T>(path: string, body: any = {}): Observable<T> {
    return this.http.post<T>(this.url(path), body, {
      headers: EInvoiceApiService.skipGlobalError,
    });
  }

  protected putRequest<T>(path: string, body: any): Observable<T> {
    return this.http.put<T>(this.url(path), body, {
      headers: EInvoiceApiService.skipGlobalError,
    });
  }

  protected deleteRequest<T>(path: string): Observable<T> {
    return this.http.delete<T>(this.url(path), {
      headers: EInvoiceApiService.skipGlobalError,
    });
  }

  /**
   * Dosya yüklemede Content-Type ELLE SET EDİLMEZ; boundary ile birlikte
   * tarayıcının yazması gerekir.
   */
  protected postFormRequest<T>(path: string, form: FormData): Observable<T> {
    return this.http.post<T>(this.url(path), form, {
      headers: EInvoiceApiService.skipGlobalError,
    });
  }

  /**
   * PDF uçları ham bayt döner.
   *
   * Yanıtın tamamı (`observe: 'response'`) alınıyor, yalnızca gövdesi değil:
   * yazdırma uçları taşma bilgisini `X-Print-Sheets` / `X-Print-Overflow`
   * başlıklarında döndürüyor ve arayüz uyarıyı oradan okuyor.
   */
  protected getBlobRequest(path: string, params?: HttpParams): Observable<HttpResponse<Blob>> {
    return this.http.get(this.url(path), {
      headers: EInvoiceApiService.skipGlobalError,
      observe: 'response',
      responseType: 'blob' as 'json',
      params,
    }) as any;
  }

  /**
   * Gövdesi JSON, yanıtı PDF olan uçlar için — tasarımcı önizlemesi
   * kaydedilmemiş yerleşimi gövdede gönderiyor.
   */
  protected postBlobRequest(path: string, body: any = {}): Observable<HttpResponse<Blob>> {
    return this.http.post(this.url(path), body, {
      headers: EInvoiceApiService.skipGlobalError,
      observe: 'response',
      responseType: 'blob' as 'json',
    }) as any;
  }

  private url(path: string): string {
    return path ? `${this.base}/${path}` : this.base;
  }
}
